import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import AuthService from "./Authorization.service";
import AccessDenied from "../components/AccessDenied/AccessDenied";
import { hasProfile, isUnpaid, type Profile } from "./accessPolicy";
import { useProfileStore } from "../../core/store/useProfileStore";

/**
 * Guarda de rota por perfil.
 *
 * O ponto é **não montar a tela**: sem isto, uma rota administrativa aberta por
 * URL montava o formulário inteiro, disparava as requisições e só então o
 * usuário via o 403. Aqui ele nem chega a sair da rota.
 *
 * O perfil vem do `useProfileStore`, que busca uma vez por carregamento de
 * página e semeia do `localStorage` — por isso navegar entre telas guardadas é
 * instantâneo, sem round-trip nem flash de `<Spin>`. O spinner só aparece no
 * primeiro carregamento frio, quando não há semente nenhuma.
 *
 * Isto é conveniência de UI, não autorização: quem autoriza é o backend, em
 * toda requisição.
 */
export interface RequireProfileProps {
  /** Perfis que podem entrar — use as listas de `accessPolicy`. */
  allow: Profile[];
  children: JSX.Element;
}

export default function RequireProfile({ allow, children }: RequireProfileProps) {
  const navigate = useNavigate();
  const profile = useProfileStore((s) => s.profile);
  const status = useProfileStore((s) => s.status);
  const load = useProfileStore((s) => s.load);

  useEffect(() => {
    // Revalida mesmo com semente: o perfil pode ter mudado no servidor.
    load();
  }, [load]);

  if (!AuthService.getAccessToken()) {
    return <Navigate to="/login" replace />;
  }

  // Só bloqueia a pintura quando não há nada em que se basear.
  if (status === "idle" || (status === "loading" && !profile)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  // Não deu para descobrir o perfil (primeiro acesso neste navegador + falha de
  // rede; um 401 teria sido redirecionado pelo interceptor). Falha ABERTA de
  // propósito: este guarda é conveniência, e trancar um admin de fora por causa
  // de uma rede instável é pior do que voltar ao comportamento antigo, em que a
  // tela montava e o backend recusava. O backend continua sendo quem autoriza.
  if (status === "error" && !profile) return children;

  if (hasProfile(profile, allow)) return children;

  // Escolhe a copy que o AccessDenied já tem, sem afirmar nada falso:
  //  - sem plano            -> "assinatura" ("Ver planos"), não é caso de cargo;
  //  - rota só de admin     -> "Área restrita ... exclusiva de administradores";
  //  - rota de outro recorte -> genérica. É o caso do `competidor` barrado em
  //    /agenda: dizer "exclusiva de administradores" seria mentira, já que
  //    `basico` entra ali.
  const adminOnly = allow.every((p) => p === "administrador" || p === "master");
  const kind = isUnpaid(profile) ? "subscription" : adminOnly ? "admin-only" : "forbidden";

  return <AccessDenied kind={kind} variant="page" onNavigate={navigate} />;
}
